import React from 'react';
import { Download } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import type { DocumentAttachment } from '../../../services/legacyApi';

type Props = {
    open: boolean;
    document: DocumentAttachment | null;
    previewUrl?: string | null;
    onOpenChange: (open: boolean) => void;
    onDownload?: (doc: DocumentAttachment) => Promise<void> | void;
};

export const DocumentPreviewDialog: React.FC<Props> = ({
    open,
    document,
    previewUrl,
    onOpenChange,
    onDownload,
}) => {
    const name = document?.original_file_name || 'Document';
    const kind = document?.file_kind || (String(document?.file_ext || '').toLowerCase() === 'pdf' ? 'pdf' : 'other');
    const canPreview = Boolean(document?.is_previewable && previewUrl);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl h-[85vh] grid-rows-[auto_1fr_auto] p-0 overflow-hidden">
                <DialogHeader className="border-b px-5 py-4">
                    <DialogTitle className="truncate pr-8">{name}</DialogTitle>
                    <DialogDescription>Preview PDF and image attachments in the ERP.</DialogDescription>
                </DialogHeader>
                <div className="min-h-0 bg-slate-100 p-4">
                    {!canPreview ? (
                        <div className="flex h-full items-center justify-center rounded border bg-white text-sm text-slate-600">
                            This file cannot be previewed in the ERP. Please download it to view.
                        </div>
                    ) : kind === 'image' ? (
                        <div className="flex h-full items-center justify-center rounded border bg-white p-3">
                            <img src={previewUrl || ''} alt={name} className="max-h-full max-w-full object-contain" />
                        </div>
                    ) : (
                        <iframe title={name} src={previewUrl || ''} className="h-full w-full rounded border bg-white" />
                    )}
                </div>
                <DialogFooter className="border-t px-5 py-3">
                    {document && onDownload && (
                        <Button variant="outline" onClick={() => onDownload(document)}>
                            <Download className="h-4 w-4 mr-2" />
                            Download
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
